import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Service {
  id: string;
  title: string;
  title_en?: string;
  title_ru?: string;
  slug: string;
  description?: string;
  content?: string;
  icon: string;
  image_url?: string;
  features: string[];
  pricing_info: {
    starting_from?: string;
    currency?: string;
    note?: string;
  };
  order_index: number;
  is_active: boolean;
  color: string;
  created_at: string;
  updated_at: string;
}

export const useServices = () => {
  return useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("is_active", true)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data as Service[];
    },
  });
};

export const useAllServices = () => {
  return useQuery({
    queryKey: ["all-services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data as Service[];
    },
  });
};

export const useServiceBySlug = (slug: string) => {
  return useQuery({
    queryKey: ["service", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) throw error;
      return data as Service;
    },
    enabled: !!slug,
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (service: Partial<Service>) => {
      const { data, error } = await supabase
        .from("services")
        .insert([service as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["all-services"] });
      toast.success("Ծառայությունը հաջողությամբ ավելացվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Service> & { id: string }) => {
      const { data, error } = await supabase
        .from("services")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["all-services"] });
      toast.success("Ծառայությունը հաջողությամբ թարմացվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("services").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["all-services"] });
      toast.success("Ծառայությունը հաջողությամբ ջնջվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};
